import { Seance } from "domain/entities/seances.entities";
import { selectSeances } from "domain/seances/seances.selectors";
import { HiLockClosed, HiPlus } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import { PageHeader } from "ui/pages/app/components/PageHeader";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { EmptyCreations } from "ui/assets/svg/EmptyCreations";
import { MySeanceCard } from "ui/pages/app/components/activities/seances/MySeanceCard";
import { selectIsPremium } from "domain/auth/auth.selectors";
import { CreateRessourceLearnMore } from "ui/assets/svg/CreateRessourceLearnMore";
import { useDisclosure } from "@chakra-ui/hooks";
import { ModalManageFolder } from "ui/pages/app/my-creations/components/ModalManageFolder";
import { selectFolders } from "domain/folders/folders.selectors";
import { FolderCard } from "ui/pages/app/my-creations/components/FolderCard";
import { useState } from "react";
import { Folder } from "domain/entities/folders.entities";
import { FolderPage } from "ui/pages/app/my-creations/components/FolderPage";
import { useWindowWidth } from "ui/utils/hooks";
import { FoldersPage } from "ui/pages/app/my-creations/components/FoldersPage";
import { FolderSelectionZone } from "ui/pages/app/my-creations/components/FolderSelectionZone";
import { FolderFinishFooter } from "ui/pages/app/my-creations/components/FolderFinishFooter";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { i18nKey } from "domain/entities/activities.entities";

export function MyCreations() {
  const seances = useAppSelector(selectSeances);
  const navigate = useNavigate();
  const isPremium = useAppSelector(selectIsPremium);
  const modalManageFolder = useDisclosure();
  const isDesktop = useWindowWidth();
  const folders = useAppSelector(selectFolders);
  const [lastCreatedFolder, setLastCreatedFolder] = useState<number | null>(
    null
  );
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [selectedFolderSeances, setSelectedFolderSeances] = useState<number[]>(
    []
  );
  const [isFoldersOpen, setIsFoldersOpen] = useState(false);

  const handleCardClick = async (seance: Seance) => {
    if (lastCreatedFolder) return;
    if (seance.active) {
      navigate(`/seances?id=${seance?.id}&isPersonal=true`);
    } else navigate("/create-seance", { state: { seanceId: seance.id } });
  };

  const handleLearnMore = () => {
    navigate("/account");
  };

  const handleSelectionClick = (seanceId: number) => {
    setSelectedFolderSeances((prevSelected) => {
      if (prevSelected.includes(seanceId)) {
        return prevSelected.filter((id) => id !== seanceId);
      } else {
        return [...prevSelected, seanceId];
      }
    });
  };

  return (
    <>
      {!selectedFolder && !isFoldersOpen && (
        <div
          className={`relative w-full px-2 pt-2 md:px-12 md:pt-8 ${
            !!lastCreatedFolder ? (isDesktop ? "pb-[152px]" : "pb-[204px]") : ""
          }`}
        >
          <PageHeader
            title={translate("ns1:Routes.MyCreations")}
            AdditionalButton={() => (
              <PrimaryButton
                height="40px"
                width="40px"
                icon={
                  isPremium ? (
                    <HiPlus size={20} color="#fff" />
                  ) : (
                    <HiLockClosed size={20} color="#fff" />
                  )
                }
                onClick={() => navigate("/create-seance")}
                disabled={!isPremium}
                iconClassName="!mr-0"
              />
            )}
          />
          {isDesktop && (
            <PrimaryButton
              height="52px"
              className="mt-4"
              icon={
                isPremium ? (
                  <HiPlus size={20} color="#fff" />
                ) : (
                  <HiLockClosed size={20} color="#fff" />
                )
              }
              onClick={() => navigate("/create-seance")}
              disabled={!isPremium}
            >
              {/* {translate("ns1:MyCreations.CreateAResource")} */}
              {translate("ns1:CreateSeance.CreateASeance")}
            </PrimaryButton>
          )}

          {!lastCreatedFolder && (
            <>
              <div className="mt-6 flex w-full items-center justify-between px-1 md:mt-10">
                <button
                  className="ms-4 flex items-center gap-3 md:gap-4"
                  onClick={modalManageFolder.onOpen}
                >
                  <span className="text-lg font-bold text-secondaryGrey-900 md:text-2xl">
                    {translate("ns1:Folders.Folders")}
                  </span>
                  <HiPlus size={24} color="#2D396B" />
                </button>
                {folders.length > (isDesktop ? 10 : 4) && (
                  <button
                    className="text-sm font-medium text-secondaryGrey-900"
                    onClick={() => setIsFoldersOpen(true)}
                  >
                    {translate("ns1:Button.ShowAll")}
                  </button>
                )}
              </div>

              {folders?.length > 0 && (
                <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-6 md:gap-4 lg:grid-cols-3 xl:grid-cols-5">
                  <>
                    {folders
                      .slice(0, isDesktop ? 10 : 4)
                      .map((folder, index) => (
                        <FolderCard
                          folder={folder}
                          key={"folder" + index}
                          onClick={() => setSelectedFolder(folder)}
                        />
                      ))}
                  </>
                </div>
              )}
            </>
          )}

          {isPremium && (
            <>
              {seances?.length > 0 ? (
                <>
                  <h2 className="mt-6 ps-5 text-lg text-secondaryGrey-900 md:mt-10 md:text-2xl">
                    {translate("ns1:MyCreations.MySeances")}
                  </h2>

                  <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                    {[...seances]
                      .sort(
                        (a, b) =>
                          new Date(b.updatedAt).getTime() -
                          new Date(a.updatedAt).getTime()
                      )
                      .map((seance, index) => (
                        <MySeanceCard
                          seance={seance}
                          key={"seance" + index}
                          onClick={() => handleCardClick(seance)}
                          folders={folders}
                          selection={!!lastCreatedFolder}
                          selected={selectedFolderSeances.includes(seance.id)}
                          onSelect={() => handleSelectionClick(seance.id)}
                        />
                      ))}
                  </div>
                </>
              ) : (
                <div className="mt-[80px] flex flex-1 flex-col items-center text-center text-base text-darkGrey-700 md:mt-[100px]">
                  <p className="font-bold">
                    {translate("ns1:MyCreations.NoRessouceCreated")}
                  </p>
                  <p className="font-medium">
                    <Trans
                      i18nKey={"MyCreations.AdviceTutorial" as i18nKey}
                      components={[
                        //eslint-disable-next-line
                        <Link
                          to="/tutorials?innizRef=4"
                          className="underline"
                          target="_blank"
                        />,
                      ]}
                    />
                  </p>
                  <div className="mt-16 w-full max-w-[500px]">
                    <EmptyCreations />
                  </div>
                </div>
              )}
            </>
          )}

          {!isPremium && (
            <div className="mt-14 flex flex-col items-center md:mt-20">
              <p className="text-center text-base font-semibold text-darkGrey-700">
                {translate("ns1:Snackbar.CreateRessource")}
              </p>
              <p className="mt-1 text-center text-sm font-normal text-darkGrey-700">
                {translate("ns1:Snackbar.TakeAdvantage")}
              </p>
              <PrimaryButton
                height="46px"
                className="mt-4"
                onClick={handleLearnMore}
              >
                {translate("ns1:Snackbar.LearnMore")}
              </PrimaryButton>

              <div className="mt-8 w-full max-w-[500px]">
                <CreateRessourceLearnMore />
              </div>
            </div>
          )}

          {!!lastCreatedFolder && (
            <div className="fixed bottom-0 left-0 flex w-full justify-end">
              <div
                style={{
                  width: isDesktop ? "calc(100% - 16rem)" : "100%",
                }}
              >
                <FolderSelectionZone selectedSeances={selectedFolderSeances} />
                <FolderFinishFooter
                  selectedFolderSeances={selectedFolderSeances}
                  onCancel={() => {
                    setLastCreatedFolder(null);
                    setSelectedFolderSeances([]);
                  }}
                  folderId={lastCreatedFolder}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {selectedFolder && (
        <FolderPage
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
        />
      )}

      {!selectedFolder && isFoldersOpen && (
        <FoldersPage
          setIsFoldersOpen={setIsFoldersOpen}
          setSelectedFolder={setSelectedFolder}
        />
      )}

      {/* <ModalCreateRessource
        isOpen={modalCreateDisclosure.isOpen}
        onClose={modalCreateDisclosure.onClose}
      /> */}

      <ModalManageFolder
        isOpen={modalManageFolder.isOpen}
        onClose={modalManageFolder.onClose}
        setLastCreatedFolder={setLastCreatedFolder}
      />
    </>
  );
}
